import React, { useState, useEffect } from 'react';
import {
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Button,
  Text,
  Box,
  RadioGroup,
  Radio,
  useToast,
  Checkbox,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem
} from '@chakra-ui/react';
import { jobApplicationService } from '../../../services/jobApplicationService';

const ApplicationFormPart2 = ({ formData, setFormData, onPrevious, onSubmit, jobCircularId, jobInfo }) => {
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY; // ensure defined in .env
  

  // Available options for Area of Interest
  const areaOfInterestOptions = [
    "Software Development",
    "DevOps", 
    "Web Development",
    "Mobile App Development",
    "Data Science",
    "Machine Learning",
    "UI/UX Design",
    "Quality Assurance",
    "Database Management",
    "Cloud Computing",
    "Cybersecurity",
    "Digital Marketing",
    "Project Management",
    "Business Analysis",
    "Other"
  ];
  

  // Load reCAPTCHA script once
  useEffect(() => {
    if (!recaptchaSiteKey) return; // skip if not configured
    if (document.getElementById('recaptcha-script')) return;
    const script = document.createElement('script');
    script.id = 'recaptcha-script';
    script.src = `https://www.google.com/recaptcha/api.js?render=explicit`;
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, [recaptchaSiteKey]);

  // Callback to be invoked by reCAPTCHA widget
  const onRecaptchaChange = (token) => {
    setRecaptchaToken(token || '');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Functions for Area of Interest tags
  const addAreaOfInterest = (interest) => {
    const currentInterests = Array.isArray(formData.areaOfInterest) ? formData.areaOfInterest : [];
    if (!currentInterests.includes(interest)) {
      handleInputChange('areaOfInterest', [...currentInterests, interest]);
    }
  };

  const removeAreaOfInterest = (interest) => {
    const currentInterests = Array.isArray(formData.areaOfInterest) ? formData.areaOfInterest : [];
    handleInputChange('areaOfInterest', currentInterests.filter(item => item !== interest));
  };

  const getAvailableOptions = () => {
    const currentInterests = Array.isArray(formData.areaOfInterest) ? formData.areaOfInterest : [];
    return areaOfInterestOptions.filter(option => !currentInterests.includes(option));
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    
    // Comprehensive Part 2 validation (all displayed fields except optional reCAPTCHA placeholder)
    const requiredFields = [
      'knowledgeAboutCompany','passion','topSkills','biggestChallenge','taskCompletionRate','hardWorking','punctuality','problemSolving','intellectualCuriosity','criticizeFreely','communicationSkills','chatgptProficiency','businessAcumen','jobSuitability','workDuration','interestReason'
    ];
    
    // Add internship-specific fields to validation if it's an internship
    if (jobInfo && jobInfo.circular_type === 'internship') {
      requiredFields.push('instituteName', 'lastCGPA', 'areaOfInterest');
    }
    
    const missingFields = requiredFields.filter(field => {
      if (field === 'areaOfInterest') {
        // Handle array field - check if it exists and has at least one selection
        return !formData[field] || (Array.isArray(formData[field]) && formData[field].length === 0);
      }
      return !formData[field];
    });
    if (!recaptchaSiteKey) {
      console.warn('RECAPTCHA site key missing; skipping token requirement (dev mode).');
    } else if (!recaptchaToken) {
      toast({
        title: 'Validation Error',
        description: 'Please complete the reCAPTCHA challenge.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (missingFields.length) {
      toast({
        title: 'Validation Error',
        description: `Please fill in all required fields. Missing: ${missingFields.slice(0,3).join(', ')}${missingFields.length>3? '…':''}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Submit to Laravel API with job circular data
      const result = await jobApplicationService.submitJobApplication({ ...formData, recaptchaToken }, jobCircularId, jobInfo);
      
      toast({
        title: "Application Submitted Successfully!",
        description: "Your job application has been submitted. We will contact you soon.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      
      // Call parent's onSubmit to close modal and reset form
      onSubmit();
      
    } catch (error) {
      console.error('Application submission error:', error);
      
      let errorMessage = "Failed to submit application. Please try again.";
      
      // Handle different error types
      if (error.message.includes('HTML instead of JSON')) {
        errorMessage = "Server configuration error. Please contact support.";
      } else if (error.message.includes('already applied')) {
        errorMessage = "You have already applied for this position.";
      } else if (error.message.includes('Circular data not found')) {
        errorMessage = "Job circular not found. Please try again.";
      } else if (error.message.includes('validation') || error.message.includes('required')) {
        errorMessage = "Please fill in all required fields correctly.";
      } else if (error.message.includes('500')) {
        errorMessage = "Server error occurred. Please try again later or contact support.";
      } else if (error.message.includes('404')) {
        errorMessage = "Job application endpoint not found. Please contact support.";
      }
      
      toast({
        title: "Submission Failed",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Box textAlign="center" mb={4}>
        <Text fontSize="2xl" fontWeight="bold" color="#2D3748" mb={2}>
          Application Form: Part 2
        </Text>
      </Box>

      <form onSubmit={handleFinalSubmit}>
        <VStack spacing={6} align="stretch">
          {/* Question 1 */}
          <FormControl isRequired>
            <FormLabel color="gray.700" fontSize="sm" mb={3}>
              1) Do you know about Business Automation Limited before this job circular? If yes, how?
            </FormLabel>
            <Textarea
              value={formData.knowledgeAboutCompany || ''}
              onChange={(e) => handleInputChange('knowledgeAboutCompany', e.target.value)}
              placeholder="Write here"
              bg="white"
              border="1px solid"
              borderColor="gray.300"
              rows={3}
            />
          </FormControl>

          {/* Question 2 */}
          <FormControl isRequired>
            <FormLabel color="gray.700" fontSize="sm" mb={3}>
              2) Please describe your passion.
            </FormLabel>
            <Textarea
              value={formData.passion || ''}
              onChange={(e) => handleInputChange('passion', e.target.value)}
              placeholder="Write here"
              bg="white"
              border="1px solid"
              borderColor="gray.300"
              rows={3}
            />
          </FormControl>

          {/* Question 3 */}
          <FormControl isRequired>
            <FormLabel color="gray.700" fontSize="sm" mb={3}>
              3) Please describe the top 3 skills that you are very good at (i.e. Microsoft Office, etc).
            </FormLabel>
            <Textarea
              value={formData.topSkills || ''}
              onChange={(e) => handleInputChange('topSkills', e.target.value)}
              placeholder="Write here"
              bg="white"
              border="1px solid"
              borderColor="gray.300"
              rows={3}
            />
          </FormControl>

          {/* Question 4 */}
          <FormControl isRequired>
            <FormLabel color="gray.700" fontSize="sm" mb={3}>
              4) What was the biggest challenge you faced in your academic, professional, or personal life? How did you overcome?
            </FormLabel>
            <Textarea
              value={formData.biggestChallenge || ''}
              onChange={(e) => handleInputChange('biggestChallenge', e.target.value)}
              placeholder="Write here"
              bg="white"
              border="1px solid"
              borderColor="gray.300"
              rows={3}
            />
          </FormControl>

          {/* Question 5 */}
          <Box>
            <Text color="gray.700" fontSize="sm" fontWeight="bold" mb={4}>
              5) Explain yourself
            </Text>
            
            <VStack spacing={4} align="stretch">
              {/* 5.1 */}
        <FormControl isRequired>
                <FormLabel color="gray.700" fontSize="sm">
          5.1) Rate how well you will be able to complete the tasks described in the job description for this circular.
                </FormLabel>
                <RadioGroup
                  value={formData.taskCompletionRate || ''}
                  onChange={(value) => handleInputChange('taskCompletionRate', value)}
                >
                  <VStack align="flex-start" spacing={2}>
                    <Radio value="Very well">Very well</Radio>
                    <Radio value="Decent">Decent</Radio>
                    <Radio value="Average">Average</Radio>
                    <Radio value="Don't know">Don't know</Radio>
                  </VStack>
                </RadioGroup>
              </FormControl>

              {/* 5.2 */}
        <FormControl isRequired>
                <FormLabel color="gray.700" fontSize="sm">
          5.2) Are you hard-working?
                </FormLabel>
                <RadioGroup
                  value={formData.hardWorking || ''}
                  onChange={(value) => handleInputChange('hardWorking', value)}
                >
                  <VStack align="flex-start" spacing={2}>
                    <Radio value="Yes">Yes</Radio>
                    <Radio value="Mostly">Mostly</Radio>
                    <Radio value="Average">Average</Radio>
                    <Radio value="Don't know">Don't know</Radio>
                  </VStack>
                </RadioGroup>
              </FormControl>

              {/* 5.3 */}
        <FormControl isRequired>
                <FormLabel color="gray.700" fontSize="sm">
          5.3) Your sense of punctuality?
                </FormLabel>
                <RadioGroup
                  value={formData.punctuality || ''}
                  onChange={(value) => handleInputChange('punctuality', value)}
                >
                  <VStack align="flex-start" spacing={2}>
                    <Radio value="Always late">Always late</Radio>
                    <Radio value="Frequently late">Frequently late</Radio>
                    <Radio value="Sometimes late">Sometimes late</Radio>
                    <Radio value="Mostly on time">Mostly on time</Radio>
                    <Radio value="Always on time">Always on time</Radio>
                  </VStack>
                </RadioGroup>
              </FormControl>

              {/* 5.4 */}
        <FormControl isRequired>
                <FormLabel color="gray.700" fontSize="sm">
          5.4) Your ability to problem-solve?
                </FormLabel>
                <RadioGroup
                  value={formData.problemSolving || ''}
                  onChange={(value) => handleInputChange('problemSolving', value)}
                >
                  <VStack align="flex-start" spacing={2}>
                    <Radio value="Very good">Very good</Radio>
                    <Radio value="Good">Good</Radio>
                    <Radio value="Average">Average</Radio>
                    <Radio value="Don't know">Don't know</Radio>
                  </VStack>
                </RadioGroup>
              </FormControl>

              {/* 5.5 */}
        <FormControl isRequired>
                <FormLabel color="gray.700" fontSize="sm">
          5.5) Your intellectual curiosity?
                </FormLabel>
                <RadioGroup
                  value={formData.intellectualCuriosity || ''}
                  onChange={(value) => handleInputChange('intellectualCuriosity', value)}
                >
                  <VStack align="flex-start" spacing={2}>
                    <Radio value="Very good">Very good</Radio>
                    <Radio value="Good">Good</Radio>
                    <Radio value="Average">Average</Radio>
                    <Radio value="Don't know">Don't know</Radio>
                  </VStack>
                </RadioGroup>
              </FormControl>

              {/* 5.6 */}
        <FormControl isRequired>
                <FormLabel color="gray.700" fontSize="sm">
          5.6) Your ability to criticize freely?
                </FormLabel>
                <RadioGroup
                  value={formData.criticizeFreely || ''}
                  onChange={(value) => handleInputChange('criticizeFreely', value)}
                >
                  <VStack align="flex-start" spacing={2}>
                    <Radio value="Very good">Very good</Radio>
                    <Radio value="Good">Good</Radio>
                    <Radio value="Average">Average</Radio>
                    <Radio value="Don't know">Don't know</Radio>
                  </VStack>
                </RadioGroup>
              </FormControl>

              {/* 5.7 */}
        <FormControl isRequired>
                <FormLabel color="gray.700" fontSize="sm">
          5.7) Your interpersonal communication skills?
                </FormLabel>
                <RadioGroup
                  value={formData.communicationSkills || ''}
                  onChange={(value) => handleInputChange('communicationSkills', value)}
                >
                  <VStack align="flex-start" spacing={2}>
                    <Radio value="Very good">Very good</Radio>
                    <Radio value="Good">Good</Radio>
                    <Radio value="Average">Average</Radio>
                    <Radio value="Don't know">Don't know</Radio>
                  </VStack>
                </RadioGroup>
              </FormControl>

              {/* 5.8 */}
        <FormControl isRequired>
                <FormLabel color="gray.700" fontSize="sm">
          5.8) How proficient are you in using ChatGPT?
                </FormLabel>
                <RadioGroup
                  value={formData.chatgptProficiency || ''}
                  onChange={(value) => handleInputChange('chatgptProficiency', value)}
                >
                  <VStack align="flex-start" spacing={2}>
                    <Radio value="Very good">Very good</Radio>
                    <Radio value="Good">Good</Radio>
                    <Radio value="Average">Average</Radio>
                    <Radio value="Don't know">Don't know</Radio>
                  </VStack>
                </RadioGroup>
              </FormControl>

              {/* 5.9 */}
        <FormControl isRequired>
                <FormLabel color="gray.700" fontSize="sm">
          5.9) Your business acumen?
                </FormLabel>
                <RadioGroup
                  value={formData.businessAcumen || ''}
                  onChange={(value) => handleInputChange('businessAcumen', value)}
                >
                  <VStack align="flex-start" spacing={2}>
                    <Radio value="Very good">Very good</Radio>
                    <Radio value="Good">Good</Radio>
                    <Radio value="Average">Average</Radio>
                    <Radio value="Don't know">Don't know</Radio>
                  </VStack>
                </RadioGroup>
              </FormControl>

              {/* 5.10 */}
        <FormControl isRequired>
                <FormLabel color="gray.700" fontSize="sm">
          5.10) How suitable are you for this job as per the job specification provided in this circular?
                </FormLabel>
                <RadioGroup
                  value={formData.jobSuitability || ''}
                  onChange={(value) => handleInputChange('jobSuitability', value)}
                >
                  <VStack align="flex-start" spacing={2}>
                    <Radio value="Very much">Very much</Radio>
                    <Radio value="Decent">Decent</Radio>
                    <Radio value="Average">Average</Radio>
                    <Radio value="Don't know">Don't know</Radio>
                  </VStack>
                </RadioGroup>
              </FormControl>
            </VStack>
          </Box>

          {/* Question 6 */}
      <FormControl isRequired>
            <FormLabel color="gray.700" fontSize="sm">
        6) How long are you willing to work here?
            </FormLabel>
            <Textarea
              value={formData.workDuration || ''}
              onChange={(e) => handleInputChange('workDuration', e.target.value)}
              placeholder="Write here"
              bg="white"
              border="1px solid"
              borderColor="gray.300"
              rows={2}
            />
          </FormControl>

          {/* Question 7 */}
      <FormControl isRequired>
            <FormLabel color="gray.700" fontSize="sm">
        7) Why are you interested in working with Business Automation Limited?
            </FormLabel>
            <Textarea
              value={formData.interestReason || ''}
              onChange={(e) => handleInputChange('interestReason', e.target.value)}
              placeholder="Write here"
              bg="white"
              border="1px solid"
              borderColor="gray.300"
              rows={3}
            />
          </FormControl>

          {/* Internship-specific fields */}
          {jobInfo && jobInfo.circular_type === 'internship' && (
            <>
              {/* Name of the Institute */}
              <FormControl isRequired>
                <FormLabel color="gray.700" fontSize="sm">Name of the Institute</FormLabel>
                <Input
                  value={formData.instituteName || ''}
                  onChange={(e) => handleInputChange('instituteName', e.target.value)}
                  placeholder="Enter your institute name"
                  bg="white"
                  border="1px solid"
                  borderColor="gray.300"
                />
              </FormControl>

              {/* Last CGPA */}
              <FormControl isRequired>
                <FormLabel color="gray.700" fontSize="sm">Last CGPA</FormLabel>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="4"
                  value={formData.lastCGPA || ''}
                  onChange={(e) => handleInputChange('lastCGPA', e.target.value)}
                  placeholder="Enter your CGPA (e.g., 3.75)"
                  bg="white"
                  border="1px solid"
                  borderColor="gray.300"
                />
              </FormControl>

              {/* Area of Interest */}
              <FormControl 
                isRequired={jobInfo && jobInfo.circular_type === 'internship'}
                isInvalid={jobInfo && jobInfo.circular_type === 'internship' && (!formData.areaOfInterest || formData.areaOfInterest.length === 0)}
              >
                <FormLabel color="gray.700" fontSize="sm">Area of Interest</FormLabel>
                
                {/* Selected tags display */}
                <Box mb={3}>
                  <Wrap>
                    {Array.isArray(formData.areaOfInterest) && formData.areaOfInterest.map((interest, index) => (
                      <WrapItem key={index}>
                        <Tag size="md" colorScheme="blue" borderRadius="full">
                          <TagLabel>{interest}</TagLabel>
                          <TagCloseButton onClick={() => removeAreaOfInterest(interest)} />
                        </Tag>
                      </WrapItem>
                    ))}
                  </Wrap>
                  {(!formData.areaOfInterest || formData.areaOfInterest.length === 0) && (
                    <Text fontSize="sm" color="gray.500">No areas selected yet</Text>
                  )}
                </Box>

                {/* Dropdown to add new interests */}
                <Select
                  placeholder="Select to add more areas of interest"
                  onChange={(e) => {
                    if (e.target.value) {
                      addAreaOfInterest(e.target.value);
                      e.target.value = ''; // Reset dropdown
                    }
                  }}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.300"
                  required={false}
                >
                  {getAvailableOptions().map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Select from dropdown to add, click X on tags to remove
                </Text>
              </FormControl>
            </>
          )}

          {/* reCAPTCHA */}
          <Box>
            {!recaptchaSiteKey && (
              <Text fontSize="xs" color="orange.500">
                REACT_APP_RECAPTCHA_SITE_KEY not set. reCAPTCHA skipped (development mode).
              </Text>
            )}
            {recaptchaSiteKey && (
              <Box
                className="g-recaptcha"
                data-sitekey={recaptchaSiteKey}
                data-callback="onReactRecaptchaSuccess"
                data-expired-callback="onReactRecaptchaExpired"
              />
            )}
          </Box>

          {/* Action Buttons */}
          <HStack spacing={4} justify="center" mt={8}>
            <Button
              onClick={onPrevious}
              bg="gray.500"
              color="white"
              px={8}
              py={3}
              borderRadius="md"
              _hover={{ bg: 'gray.600' }}
              fontWeight="bold"
            >
              Back
            </Button>
            <Button
              type="submit"
              bg="#4C63B6"
              color="white"
              px={8}
              py={3}
              borderRadius="md"
              _hover={{ bg: '#3A4D8F' }}
              fontWeight="bold"
              isLoading={isSubmitting}
              loadingText="Submitting..."
              disabled={isSubmitting}
            >
              Send
            </Button>
          </HStack>
        </VStack>
      </form>
    </VStack>
  );
};

export default ApplicationFormPart2;
